import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Package,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Plus,
  CreditCard
} from 'lucide-react';
import { adPackageApi } from '../services/api';
import { AdPurchase } from '../types';
import { extractApiError } from '../utils/errorUtils';

export default function VendorAdPurchaseHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchases, setPurchases] = useState<AdPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPurchase, setSelectedPurchase] = useState<AdPurchase | null>(null);
  const [processingPayment, setProcessingPayment] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const pageSize = 20;

  const loadPurchases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adPackageApi.getMyPurchases({
        page: currentPage,
        page_size: pageSize
      });
      setPurchases(response.items);
      setTotalPages(response.total_pages);
      setTotalPurchases(response.total);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load purchases:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  // Handle success/cancel redirect from Stripe
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const timeouts: NodeJS.Timeout[] = [];

    if (success === 'true') {
      setSuccessMessage('Payment successful! Your advertisement is now active.');
      // Clear URL parameters
      navigate('/vendor/ad-purchases', { replace: true });
      // Auto-dismiss after 5 seconds
      timeouts.push(setTimeout(() => setSuccessMessage(null), 5000));
      // Refresh purchases after a brief delay to allow webhook to process
      timeouts.push(setTimeout(() => {
        loadPurchases();
      }, 1500));
    } else if (canceled === 'true') {
      setError('Payment was canceled. You can try again anytime.');
      navigate('/vendor/ad-purchases', { replace: true });
      // Auto-dismiss after 5 seconds
      timeouts.push(setTimeout(() => setError(null), 5000));
    }

    // Cleanup function to clear all timeouts if component unmounts
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [searchParams, navigate, loadPurchases]);

  const handlePayNow = async (purchaseId: number) => {
    try {
      setProcessingPayment(purchaseId);
      setError(null);

      // Create Stripe Checkout Session
      const { checkout_url } = await adPackageApi.createCheckoutSession(purchaseId);

      // Redirect to Stripe
      window.location.href = checkout_url;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to create checkout session:', apiError);
      setError(apiError.message);
      setProcessingPayment(null);
    }
  };

  const formatPrice = (cents: number, currency: string) => {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'refunded':
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'failed':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'refunded':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Advertisement Purchases</h1>
          <p className="text-gray-600 mt-2">View your advertisement purchase history and status</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/vendor/ad-purchase')}
          className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark"
        >
          <Plus className="h-4 w-4" />
          <span>New Advertisement</span>
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-start">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{error}</p>
            <button
              type="button"
              onClick={loadPurchases}
              className="text-sm text-red-600 hover:text-red-800 underline mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading your purchases...</p>
        </div>
      )}

      {/* Purchases List */}
      {!loading && purchases.length > 0 && (
        <div className="space-y-4">
          {purchases.map((purchase) => (
            <div
              key={purchase.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                {/* Purchase Info */}
                <div className="flex-1">
                  <div className="flex items-start mb-2">
                    {getStatusIcon(purchase.payment_status)}
                    <div className="ml-3 flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {purchase.advertisement_title || 'Advertisement Purchase'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Package: {purchase.package_name || 'Unknown Package'}
                      </p>
                      {purchase.vendor_name && (
                        <p className="text-sm text-gray-600">Vendor: {purchase.vendor_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-gray-500">Purchase Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {purchase.purchase_date
                          ? formatDate(purchase.purchase_date)
                          : 'N/A'}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">Amount</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatPrice(purchase.amount_cents, purchase.currency)}
                      </p>
                    </div>

                    {purchase.start_date && (
                      <div>
                        <p className="text-xs text-gray-500">Start Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(purchase.start_date)}
                        </p>
                      </div>
                    )}

                    {purchase.end_date && (
                      <div>
                        <p className="text-xs text-gray-500">End Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(purchase.end_date)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Payment Method and Transaction ID */}
                  <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-500">
                    {purchase.payment_method && (
                      <div>
                        <span className="font-medium">Payment:</span> {purchase.payment_method}
                      </div>
                    )}
                    {purchase.transaction_id && (
                      <div>
                        <span className="font-medium">Transaction:</span> {purchase.transaction_id}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status and Actions */}
                <div className="mt-4 md:mt-0 md:ml-6 flex flex-col items-end space-y-3">
                  <span className={getStatusBadge(purchase.payment_status)}>
                    {purchase.payment_status.charAt(0).toUpperCase() +
                      purchase.payment_status.slice(1)}
                  </span>

                  {/* Pay Now Button for Pending Purchases */}
                  {purchase.payment_status === 'pending' && (
                    <button
                      type="button"
                      onClick={() => handlePayNow(purchase.id)}
                      disabled={processingPayment === purchase.id}
                      className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard className="h-4 w-4" />
                      <span>
                        {processingPayment === purchase.id ? 'Processing...' : 'Pay Now'}
                      </span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setSelectedPurchase(purchase)}
                    className="flex items-center space-x-1 text-sm text-secondary hover:text-primary-dark"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Details</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && purchases.length === 0 && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No advertisement purchases yet</p>
          <button
            type="button"
            onClick={() => navigate('/vendor/ad-purchase')}
            className="text-secondary hover:text-primary-dark font-medium"
          >
            Purchase your first advertisement
          </button>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {purchases.length} of {totalPurchases} purchases
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {selectedPurchase && (
        <PurchaseDetailsModal
          purchase={selectedPurchase}
          onClose={() => setSelectedPurchase(null)}
        />
      )}
    </div>
  );
}

// Purchase Details Modal Component
interface PurchaseDetailsModalProps {
  purchase: AdPurchase;
  onClose: () => void;
}

function PurchaseDetailsModal({ purchase, onClose }: PurchaseDetailsModalProps) {
  const formatPrice = (cents: number, currency: string) => {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'refunded':
        return 'text-orange-600';
      default:
        return 'text-yellow-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Purchase Details</h2>
              <p className="text-sm text-gray-600 mt-1">
                Purchase ID: {purchase.id}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Status */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Payment Status</h3>
              <p className={`text-lg font-semibold ${getStatusColor(purchase.payment_status)}`}>
                {purchase.payment_status.charAt(0).toUpperCase() +
                  purchase.payment_status.slice(1)}
              </p>
            </div>

            {/* Package Details */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Package</h3>
              <p className="text-base font-medium text-gray-900">
                {purchase.package_name || 'Unknown Package'}
              </p>
            </div>

            {/* Advertisement Details */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Advertisement</h3>
              <p className="text-base font-medium text-gray-900">
                {purchase.advertisement_title || 'No title'}
              </p>
            </div>

            {/* Vendor */}
            {purchase.vendor_name && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Vendor</h3>
                <p className="text-base text-gray-900">{purchase.vendor_name}</p>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Purchase Date
                </h3>
                <p className="text-base text-gray-900">
                  {purchase.purchase_date ? formatDate(purchase.purchase_date) : 'N/A'}
                </p>
              </div>

              {purchase.start_date && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Start Date
                  </h3>
                  <p className="text-base text-gray-900">{formatDate(purchase.start_date)}</p>
                </div>
              )}

              {purchase.end_date && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    End Date
                  </h3>
                  <p className="text-base text-gray-900">{formatDate(purchase.end_date)}</p>
                </div>
              )}
            </div>

            {/* Payment Details */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                Payment Information
              </h3>
              <div className="bg-gray-50 rounded-md p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatPrice(purchase.amount_cents, purchase.currency)}
                  </span>
                </div>
                {purchase.payment_method && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Payment Method:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {purchase.payment_method}
                    </span>
                  </div>
                )}
                {purchase.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Transaction ID:</span>
                    <span className="text-sm font-mono text-xs text-gray-900">
                      {purchase.transaction_id}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
              {purchase.created_at && (
                <div>
                  Created: {new Date(purchase.created_at).toLocaleString()}
                </div>
              )}
              {purchase.updated_at && (
                <div>
                  Last Updated: {new Date(purchase.updated_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
